import * as React from "react";
import KanopiLogo from "../static/images/kanopi-logo-white.svg";
import { Block } from "baseui/block";
import { CustomItem, LeftNavItem } from "./LeftNavItem";
import { LeftNavHeader } from "./LeftNavHeader";
import { SidePadder } from "kanopi-components/SpaceWrapper/SidePadder";
import { useBreakpoint } from "../useBreakpoint";
import { useStyletron } from "../theme/useStyletron";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import {
  Item,
  Navigation,
  NavigationOverrides,
  NavItemProps,
} from "baseui/side-navigation";

type LeftNavProps = {
  expanded?: boolean;
  title: string;
  logoSrc?: string;
  logoAltText?: string;
  navigationItems: Item[];
  isNotOverlay?: boolean;
  activeItemId?: string;
  expandedWidth?: string;
  collapsedWidth?: string;
  mainItems?: NavItemProps[];
  userItems?: NavItemProps[];
  navigate?: (url: string) => void;
  onOpenClose?: (open: boolean) => void;
};

export function LeftNav(props: LeftNavProps) {
  const [, theme] = useStyletron();
  const { isSmall } = useBreakpoint();

  const {
    expanded = true,
    title,
    logoSrc = KanopiLogo,
    logoAltText,
    navigationItems,
    mainItems = [],
    userItems = [],
    activeItemId,
    expandedWidth = "246px",
    collapsedWidth = theme.sizing.scale1200,
    navigate,
    onOpenClose,
  } = props;

  const {
    backgroundColor,
    textColor,
    hoverBackgroundColor,
    hoverTextColor,
    activeBackgroundColor,
    activeTextColor,
    borderSelected,
  } = theme?.componentOverrides?.leftNav || {};

  const navigationOverrides: NavigationOverrides = {
    Root: {
      style: {
        color: "inherit",
        borderTopStyle: "solid",
        borderTopWidth: "0.5px",
        borderTopColor: hoverBackgroundColor,
      },
    },
    NavItem: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      component: ({ children: title, ...props }) => {
        return <LeftNavItem {...props} collapsed={!open} />;
      },
      style: ({ $active }) => {
        if ($active) {
          return {
            backgroundColor: activeBackgroundColor,
            borderLeftColor: borderSelected,
            backgroundImage: "none",
            color: activeTextColor,
            ":hover": {
              backgroundColor: hoverBackgroundColor,
              color: hoverTextColor,
            },
          };
        } else {
          return {
            backgroundColor: backgroundColor,
            borderBottomStyle: "solid",
            borderBottomWidth: "0.5px",
            borderBottomColor: hoverBackgroundColor,
            color: textColor,
            ":hover": {
              borderLeftColor: borderSelected,
              backgroundColor: hoverBackgroundColor,
              color: hoverTextColor,
            },
          };
        }
      },
    },
  };

  const [open, setOpen] = React.useState<boolean>();
  const onOpenCloseToggle = () => {
    const _open = !open;
    setOpen(_open);
    onOpenClose?.(_open);
  };
  React.useEffect(() => {
    setOpen(isSmall ? false : expanded);
  }, [isSmall]);

  const height = !isSmall ? "100vh" : open ? "100vh" : "auto";

  return (
    <Block
      color={textColor}
      backgroundColor={backgroundColor}
      width={isSmall ? "100%" : open ? expandedWidth : collapsedWidth}
    >
      {isSmall && !open ? (
        <LeftNavHeader
          logoSrc={logoSrc}
          logoAltText={logoAltText}
          title={title}
          color={textColor}
          onOpenCloseToggle={onOpenCloseToggle}
          open={true}
          backgroundColor={backgroundColor}
          kind="small"
          hoverBackgroundColor={hoverBackgroundColor}
          hoverTextColor={hoverTextColor}
          mainItems={mainItems}
          userItems={userItems}
        />
      ) : (
        <Block
          backgroundColor={backgroundColor}
          width={isSmall ? "100%" : open ? expandedWidth : collapsedWidth}
          color={textColor}
          height={height}
          paddingTop={theme.sizing.scale900}
        >
          <VerticalSpacer>
            <SidePadder
              padding={!open ? "none" : "section"}
              overrides={{
                Block: {
                  style: {
                    borderLeftWidth: theme.sizing.scale100,
                    borderLeftStyle: "solid",
                    borderLeftColor: backgroundColor,
                  },
                },
              }}
            >
              <LeftNavHeader
                title={title}
                logoSrc={logoSrc}
                logoAltText={logoAltText}
                color={textColor}
                onOpenCloseToggle={onOpenCloseToggle}
                open={open}
                backgroundColor={backgroundColor}
                hoverBackgroundColor={hoverBackgroundColor}
                hoverTextColor={hoverTextColor}
              />
            </SidePadder>
            <Navigation
              items={navigationItems}
              activeItemId={activeItemId}
              onChange={({
                event,
                item,
              }: {
                event: Event | KeyboardEvent;
                item: CustomItem;
              }) => {
                // Prevent page reload. Click is handled by Nav item.
                event.preventDefault();
                if (item?.url) {
                  navigate?.(item.url);
                } else {
                  item?.onClick?.(event, item);
                }
                if (isSmall) {
                  setOpen(false);
                }
              }}
              overrides={navigationOverrides}
            />
          </VerticalSpacer>
        </Block>
      )}
    </Block>
  );
}
